<template lang="pug">
    .stepper.product-form(:class="{ 'is-embedded': embedded }")
        el-steps(v-if="form.type === 'product'" :active="step" align-center)
            el-step(
                :title="$t('Product')"
                :description="form.name"
                class="clickable"
                @click.native="goToStep(1)"
            )
            el-step(
                :title="$t('Price')"
                :description="mainPrice"
                :class="{ clickable: validUntilStep >= 2 }"
                @click.native="goToStep(2)"
            )
            el-step(
                :title="$t('Attributes')"
                :class="{ clickable: validUntilStep >= 3 }"
                @click.native="goToStep(3)"
            )
            el-step(
                :title="$t('Stock')"
                :class="{ clickable: validUntilStep >= 4 }"
                @click.native="goToStep(4)"
            )
        el-steps(v-else-if="form.type === 'coupon'" :active="step" align-center)
            el-step(
                :title="$t('Product')"
                :description="form.name"
                class="clickable"
                @click.native="goToStep(1)"
            )
            el-step(
                :title="$t('Price')"
                :description="mainPrice"
                :class="{ clickable: validUntilStep >= 2 }"
                @click.native="goToStep(2)"
            )
        el-steps(v-else-if="form.type === 'event'" :active="step" align-center)
            el-step(
                :title="$t('Product')"
                :description="form.name"
                class="clickable"
                @click.native="goToStep(1)"
            )
            el-step(
                :title="$t('Event')"
                :description="sessionSummary"
                :class="{ clickable: validUntilStep >= 2 }"
                @click.native="goToStep(2)"
            )
            el-step(
                :title="$t('Price')"
                :description="mainPrice"
                :class="{ clickable: validUntilStep >= 3 }"
                @click.native="goToStep(3)"
            )
        .wrapper
            el-form(label-position="top" :model="form" :rules="rules" ref="form")
                el-card(v-if="form.type === 'product'" shadow="always" v-loading="loading")
                    .steps(v-if="! loading")
                        transition(name="slide-decent-vertical" mode="out-in")
                            ProductStep(v-if="step === 1" :form="form" :rules="rules")
                            PricesStep(v-else-if="step === 2" :form="form" :rules="rules")
                            PropertiesStep(v-else-if="step === 3" :form="form" :rules="rules")
                            StockStep(v-else-if="step === 4" :form="form" :rules="rules")
                el-card(v-else-if="form.type === 'coupon'" shadow="always" v-loading="loading")
                    .steps(v-if="! loading")
                        transition(name="slide-decent-vertical" mode="out-in")
                            ProductStep(v-if="step === 1" :form="form" :rules="rules")
                            PricesStep(v-else-if="step === 2" :form="form" :rules="rules")
                //- Embedded in a drawer the surrounding .block-content already IS the panel, so a
                //- card here would draw a second box inside the first.
                component(
                    v-else-if="form.type === 'event'"
                    :is="embedded ? 'div' : 'el-card'"
                    :shadow="embedded ? undefined : 'always'"
                    v-loading="loading"
                )
                    .steps(v-if="! loading")
                        transition(name="slide-decent-vertical" mode="out-in")
                            ProductStep(v-if="step === 1" :form="form" :rules="rules" :type-locked="!!prefill")
                            EventStep(v-else-if="step === 2" :form="form" :rules="rules" :event-locked="!!lockedEventId")
                            PricesStep(v-else-if="step === 3" :form="form" :rules="rules")
                //- Embedded, the host renders these in its own .drawer-footer instead.
                .buttons(v-if="! embedded")
                    el-button(
                        round v-if="! loading"
                        :disabled="step <= 1"
                        @click="prev"
                    ) {{ $t('Previous') }}
                    el-button(
                        round v-if="! loading && step < lastStep"
                        type="success"
                        :disabled="! canAdvance"
                        @click="next"
                    ) {{ $t('Next') }}
                    span(v-else-if="! loading && step == lastStep")
                        el-button(
                            round
                            type="primary"
                            icon="el-icon-fa-check fas"
                            :loading="saving"
                            @click="submit($event, 'close')"
                        ) {{ $t('Save and close') }}
                        el-button(
                            round
                            type="success"
                            icon="el-icon-fa-save fas"
                            :loading="saving"
                            @click="submit($event, 'new')"
                        ) {{ $t('Save and new') }}
</template>

<i18n>
{
    "de": {
        "Product could not be saved.": "Produkt konnte nicht gespeichert werden.",
        "Could not load the product options": "Produktoptionen konnten nicht geladen werden",
        "Product saved successfully!": "Produkt erfolgreich gespeichert!",
        "Product": "Produkt",
        "Price": "Preis",
        "Attributes": "Produktmerkmale",
        "Stock": "Bestand",
        "Event": "Veranstaltung",
        "Previous": "Zurück",
        "Next": "Weiter",
        "{count} dates": "ein Termin | {count} Termine",
        "Please select at least one date": "Bitte mindestens einen Termin auswählen",
        "A ticket has to admit its holder to something.": "Ein Ticket muss für mindestens einen Termin gelten."
    },
    "en": {
        "{count} dates": "one date | {count} dates"
    }
}
</i18n>

<script>
import uniqid from 'uniqid';

import store from '@/store';
import router from '@/router';
import { moneyFormat } from '@/filters';
import _ from 'lodash';

import pinia from '@/pinia';
import { useSetupStore, useShopProductStore } from '@/stores';

import ProductStep from './Steps/Product';
import PricesStep from './Steps/Prices';
import PropertiesStep from './Steps/Properties';
import StockStep from './Steps/Stock';
import EventStep from './Steps/Event';

export default {
    name: 'NewProductStepper',
    props: {
        loading: {
            type: Boolean,
            default: false,
        },
        // Seed values when the stepper is opened somewhere other than the product list — from an
        // event, to sell tickets for it. Also switches the stepper to embedded behaviour: it emits
        // instead of navigating, so the merchant stays where they were.
        prefill: {
            type: Object,
            default: null,
        },
    },
    data() {
        const shopProductStore = useShopProductStore(pinia);
        const setupStore = useSetupStore();
        const options = shopProductStore.options || [];

        let tax_rate = null
        let tax = _.get(setupStore.activeCms, 'shop.defaultTax')

        if (options.tax.items.filter(el => el.default === true && el.reduced === false)) {
            tax_rate = 'regular'
            tax = null
        }

        return {
            step: 1,
            validUntilStep: 1,
            saving: false,
            listRoute: { name: 'product-list' },
            form: {
                id: uniqid('new:'),
                nr: '',
                type: 'product',
                status: 'enabled',
                condition: 'new',
                brand: '',
                name: '',
                teaser: '',
                description: '',
                pos_favorite: false,
                shop: {
                    id: _.get(setupStore.activeCms, 'shop.id'),
                },
                categories: [],
                media: [],
                prices: [
                    {
                        id: uniqid('new:'),
                        type: 'main',
                        value: '',
                        tax,
                        tax_rate,
                        price_on_request: false,
                    },
                ],
                coupon: undefined,
                propSet: null,
                manage_stock: true,
                out_of_stock_threshold: 0,
                propertyPivots: [],
                articleQty: 0,
                articleStock: null,
                slots: [],
                // Last, so seeded values win over the defaults above.
                ...(this.prefill || {}),
            },
        };
    },
    components: {
        ProductStep,
        PricesStep,
        PropertiesStep,
        StockStep,
        EventStep,
    },
    computed: {
        // Opened from somewhere other than the product list — a drawer that supplies its own
        // panel and its own footer.
        embedded() {
            return !!this.prefill;
        },
        // Everything the host needs to drive the footer buttons it renders on our behalf.
        footerState() {
            return {
                step: this.step,
                lastStep: this.lastStep,
                saving: this.saving,
                loading: this.loading,
                canAdvance: this.canAdvance,
            };
        },
        /**
         * A ticket to no dates is not a product - the backend refuses it outright. Catch it on the
         * step where the dates are picked instead of at the very end of the flow.
         */
        canAdvance() {
            if (this.form.type === 'event' && this.step === 2) {
                return ((this.form.slots || []).length > 0);
            }

            return true;
        },
        lockedEventId() {
            return _.get(this.prefill, 'event.id') || null;
        },
        sessionSummary() {
            const count = (this.form.slots || []).length;
            if (!count) return '';
            return this.$tc('{count} dates', count, { count });
        },
        mainPrice() {
            const price = this.form.prices.find((el) => el.type === 'main');
            const customerGroup = _.get(price, 'customerGroup') || _.get(this.activeShop, 'defaultCustomerGroup');

            const code = _.get(customerGroup, 'currency.iso4217');

            if (price && price.value && code) return moneyFormat(price.value, code);
        },
        rules() {
            const rules = {
                nr: `required|unique_product_nr:"shop":${this.activeShop.id}`,
                status: 'required',
                condition: 'required',
                brand: 'max:70',
                name: 'required',
                prices: [
                    {
                        type: 'required',
                        value: 'required|min_value:0',
                        // tax: { value: 'required' },
                    },
                ],
                propSet: { id: 'required' },
            };

            // A coupon and a ticket render none of these, so validating them would block Save on
            // fields the merchant cannot see, let alone correct.
            if (['coupon', 'event'].includes(this.form.type)) {
                delete rules.propSet;
                delete rules.condition;
                delete rules.brand;
            }

            return rules;
        },
        lastStep() {
            let lastStep = 4;
            if (this.form.type === 'coupon') lastStep = 2;
            if (this.form.type === 'event') lastStep = 3;
            return lastStep;
        },
        activeShop() {
            return this.activeCms.shop;
        },
        setupStore() {
            return useSetupStore();
        },
        shopProductStore() {
            return useShopProductStore(pinia);
        },
        activeCms() {
            return this.setupStore.activeCms;
        },
        options() {
            return this.shopProductStore.options;
        },
    },
    watch: {
        // $refs are not reactive, so the host cannot read our step directly - push it instead.
        footerState: {
            immediate: true,
            deep: true,
            handler(state) {
                if (this.embedded) this.$emit('state', state);
            },
        },
    },
    created() {
        this.loadOptions();
    },
    methods: {
        /**
         * The stepper has always read its selects (categories, taxes, customer groups, ...) out of
         * the shop product store without ever filling it - Product/Form.vue does that, and the
         * stepper happened to be rendered on a route that had already run it. Opened from the event
         * form instead, nothing had, so every select came up empty. Load them here so the component
         * stands on its own, and skip it when a host already did the work.
         */
        loadOptions() {
            if (_.get(this.options, 'category.items.length')) return;

            const shopID = _.get(this.activeShop, 'id');
            const cmsID = _.get(this.activeCms, 'id');

            if (!shopID || !cmsID) return;

            const store = this.shopProductStore;

            Promise.all([
                store.fetchCategoryOptions(shopID),
                store.fetchTaxOptions(shopID),
                store.fetchCustomerGroupOptions(shopID),
                store.fetchPropSetOptions(shopID),
                store.fetchStockOptions(shopID),
                store.fetchTagOptions(shopID),
                store.fetchMenuOptions(cmsID),
            ]).catch(error => {
                this.$notify.error({
                    title: this.$t('Could not load the product options'),
                    iconClass: 'fas el-icon-fa-exclamation-triangle',
                    position: 'bottom-left',
                    customClass: 'dark',
                    duration: 8000,
                });

                throw error;
            });
        },

        next() {
            // The dates are not an el-form field, so vee-validate cannot speak for them.
            if (!this.canAdvance) {
                this.$notify.error({
                    title: this.$t('Please select at least one date'),
                    message: this.$t('A ticket has to admit its holder to something.'),
                    iconClass: 'fas el-icon-fa-times',
                    position: 'bottom-left',
                    customClass: 'dark',
                    duration: 8000,
                });

                return;
            }

            // eslint-disable-next-line
            this.$refs.form.validate((valid, errors) => {
                if (valid) {
                    ++this.step;
                    this.validUntilStep = this.step;
                }
            });
        },
        prev() {
            --this.step;
        },
        goToStep(step) {
            if (step <= this.validUntilStep) this.step = step;
        },
        submit(event, action = 'new') {
            // eslint-disable-next-line
            this.$refs.form.validate((valid, errors) => {
                if (valid) {
                    this.saving = true;

                    let product = _.cloneDeep(this.form);
                    switch (product.type) {
                        case 'coupon':
                            product.propSet = null;
                            product.manage_stock = false;
                            product.out_of_stock_threshold = 0;
                            product.propertyPivots = [];
                            product.articleQty = 0;
                            product.articleStock = null;
                            break;

                        case 'event':
                            // Capacity lives on the date, not in a stock row; a ticket has no
                            // properties and nothing gets posted.
                            product.propSet = null;
                            product.manage_stock = false;
                            product.out_of_stock_threshold = 0;
                            product.propertyPivots = [];
                            product.articleQty = 0;
                            product.articleStock = null;
                            product.shipping_points = 0;
                            // The slots belong to the variant, like a coupon does - the product
                            // itself has no set of its own, so they travel on the article the
                            // stepper is asking for.
                            product.articles = [{
                                sku: product.nr,
                                slots: product.slots || [],
                            }];
                            delete product.slots;
                            if (product.coupon) delete product.coupon;
                            if (product.event) delete product.event;
                            break;
                        default:
                            if (product.coupon) delete product.coupon;
                            product.propertyPivots.forEach((pivot) => {
                                delete pivot.property.default;
                            });
                            break;
                    }

                    this.create(product)
                        .then(() => {
                            this.resetForm();

                            this.$notify.success({
                                title: this.$t('Product saved successfully!'),
                                iconClass: 'fas el-icon-fa-check',
                                position: 'bottom-left',
                                customClass: 'dark',
                                duration: 8000,
                            });

                            switch (action) {
                                case 'new':
                                    this.step = 1;
                                    break;

                                case 'close':
                                    // Embedded in a drawer, the merchant is in the middle of
                                    // something else — hand back rather than navigate them away.
                                    if (this.prefill) this.$emit('saved');
                                    else router.push(this.listRoute);
                                    this.step = 1;
                                    break;
                            }
                        })
                        .catch((err) => {
                            /* eslint no-console: ["error", { allow: ["error"] }] */
                            console.error(err);

                            this.$notify.error({
                                title: this.$t('An error occurred!'),
                                message: this.$t('Product could not be saved.'),
                                iconClass: 'fas el-icon-fa-times',
                                position: 'bottom-left',
                                customClass: 'dark',
                                duration: 8000,
                            });
                        })
                        .finally(() => {
                            this.saving = false;
                        });
                }
            });
        },
        resetForm() {
            const options = this.shopProductStore.options || [];

            let tax_rate = null
            let tax = _.get(this.activeCms, 'shop.defaultTax')

            if (options.tax.items.filter(el => el.default === true && el.reduced === false)) {
                tax_rate = 'regular'
                tax = null
            }

            this.form = {
                id: uniqid('new:'),
                nr: '',
                type: 'product',
                status: 'enabled',
                // Seeded here as well as in data(): "Save and new" runs this, and condition is
                // required but hidden for coupons and tickets - an unset one would block Save with
                // no field on screen to fix it.
                condition: 'new',
                brand: '',
                name: '',
                teaser: '',
                description: '',
                pos_favorite: false,
                shop: {
                    id: _.get(this.activeCms, 'shop.id'),
                },
                categories: [],
                media: [],
                prices: [
                    {
                        id: uniqid('new:'),
                        type: 'main',
                        value: '',
                        tax,
                        tax_rate,
                        price_on_request: false,
                    },
                ],
                coupon: undefined,
                propSet: null,
                manage_stock: true,
                out_of_stock_threshold: 0,
                propertyPivots: [],
                articleQty: 0,
                articleStock: null,
                slots: [],
            };

            if (this.prefill) this.form = { ...this.form, ...this.prefill };
            // this.$refs.form.resetFields()
        },
        create(product) {
            return this.shopProductStore.create(product);
        },
    },
};
</script>

<style lang="sass" scoped>
.wrapper
    margin: 0 6%
    .el-card
        margin: 15px 0
        min-height: 135px
    .buttons
        display: flex
        justify-content: space-between

// Embedded in a drawer the panel and the side gutters come from the drawer itself, so the
// stepper stops supplying its own.
.stepper.is-embedded
    .wrapper
        margin: 0
    .el-steps
        margin-bottom: 20px

.el-step.clickable
    cursor: pointer
    pointer-events: auto
    ::v-deep
        .el-step__head,
        .el-step__title,
        .el-step__description
            color: $--shop-color-primary
            border-color: $--shop-color-primary
            font-weight: normal
.el-step
    pointer-events: none
    ::v-deep
        .el-step__title
            font-weight: normal
            line-height: 1.3
            margin-bottom: 6px
</style>
